import React, {useCallback, useMemo, useState} from 'react';

import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {ConfirmFormPopover} from 'app/general/widgets/ConfirmPopover';
import {useBulkRemoveUsers} from 'app/user/api';
import {gt, ngt} from 'app/utils/i18n';

import type {
  ActionButtonBuilderType,
  ActionMenuItemType,
} from 'app/general/widgets/ActionsButton';
import type {UserSerializedType} from 'sweet-types';

interface Props {
  children?: React.ReactElement | ActionButtonBuilderType;
  users: Array<UserSerializedType>;
  enableBulkSelect: boolean;
}

export function UserBulkActionsButton({
  children,
  users,
  enableBulkSelect,
}: Props): JSX.Element | null {
  if (!enableBulkSelect) {
    return null;
  }

  const [isShowing, setShow] = useState<null | 'remove'>(null);

  const close = () => setShow(null);

  const [currButtonRef, setCurrButtonRef] = useState<null | HTMLElement>(null);

  const setButtonRef = useCallback((newButtonRef) => {
    if (newButtonRef != currButtonRef) {
      setCurrButtonRef(newButtonRef);
    }
  }, []);

  const [bulkRemoveUsers] = useBulkRemoveUsers();

  const availableActions = useMemo(
    (): Array<ActionMenuItemType> => [
      {
        text: gt('Remove (%s selected)', users.length),
        icon: 'fa fa-times',
        key: 'remove',
        disabled: users.length === 0,
        callback: () => setShow('remove'),
      },
    ],
    [users.length]
  );

  return (
    <>
      <ActionsButton ref={setButtonRef} actions={availableActions}>
        {children}
      </ActionsButton>
      {isShowing === 'remove' && currButtonRef != null ? (
        <ConfirmFormPopover
          overlayProps={{
            target: currButtonRef as HTMLElement,
            trigger: 'legacy',
            placement: 'bottom' as const,
            isOpen: true,
            toggle: close,
          }}
          text={ngt(
            'Remove %s member from the account?',
            'Remove %s members from the account?',
            users.length
          )}
          confirmText={gt('Yes')}
          busyText={gt('Removing...')}
          onConfirm={() =>
            bulkRemoveUsers({users: users.map((user) => user.url)}).unwrap()
          }
          isIdle={true}
        />
      ) : null}
    </>
  );
}
